// ===== 1. Imports =====
import React, { useEffect, useState } from "react";
import emailjs from "emailjs-com";
import { Trash2, Mail, PlusCircle, LogIn } from "lucide-react";
import Modal from "../components/Modal";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  getDocs,
} from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { EMAILJS_CONFIG } from "../emailjsConfig";

// ===== 2. Helper: Generar contraseña segura =====
function generatePassword(length = 10) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  return Array.from({ length }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join("");
}

// ===== 3. Componente Principal =====
export default function ProjectsPage({ onEnterProject }) {
  // ===== 4. Estados =====
  const [projects, setProjects] = useState([]);
  const [departamentos, setDepartamentos] = useState([]); // ✅ nuevos
  const [projectName, setProjectName] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminRole, setAdminRole] = useState("PM");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedDept, setSelectedDept] = useState("");

  // ===== 5. Firestore: Escucha de proyectos =====
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "projects"), (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProjects(list);
    });
    return () => unsub();
  }, []);

  // ===== 6. Cargar departamentos del proyecto seleccionado =====
  useEffect(() => {
    if (!selectedProject) return;
    const deptRef = collection(
      db,
      "projects",
      selectedProject.code,
      "departamentos"
    );
    const unsub = onSnapshot(deptRef, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) =>
        String(a.name).localeCompare(b.name, "es", { sensitivity: "base" })
      );
      setDepartamentos(list);
    });
    return () => unsub();
  }, [selectedProject]);

  // ===== 7. Notificación =====
  const notify = (msg, error = false) => {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 3500);
  };

  // ===== 8. Configuración EmailJS =====
  const { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } = EMAILJS_CONFIG;

  // ===== 9. Crear nuevo proyecto =====
  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectName || !projectCode || !adminEmail || !adminName) {
      notify("Completa todos los campos", true);
      return;
    }
    const codeTrimmed = projectCode.trim().toUpperCase();
    const tempPassword = generatePassword(10);
    try {
      setSending(true);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        adminEmail.trim(),
        tempPassword
      );
      const newProject = {
        name: projectName.trim(),
        code: codeTrimmed,
        admin: adminEmail.trim(),
        adminName: adminName.trim(),
        role: adminRole,
        password: tempPassword,
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, "projects", codeTrimmed), newProject);
      const usersRef = doc(
        db,
        "projects",
        codeTrimmed,
        "users",
        adminEmail.trim()
      );
      await setDoc(usersRef, {
        name: adminName.trim(),
        email: adminEmail.trim(),
        role: adminRole,
        password: tempPassword,
        department: "",
        createdAt: serverTimestamp(),
      });
      const deptRef = doc(
        db,
        "projects",
        codeTrimmed,
        "departamentos",
        "General"
      );
      await setDoc(deptRef, {
        name: "General",
        createdAt: serverTimestamp(),
      });
      const placeholders = [
        { path: ["suppliers", "_init"], data: { placeholder: true } },
        { path: ["budget", "_init"], data: { placeholder: true } },
        { path: ["pos", "_init"], data: { placeholder: true } },
      ];
      for (const { path, data } of placeholders) {
        await setDoc(doc(db, "projects", codeTrimmed, ...path), data);
      }
      await sendEmail({
        name: projectName.trim(),
        code: codeTrimmed,
        admin: adminEmail.trim(),
        password: tempPassword,
      });
      notify(`Proyecto "${projectName}" creado correctamente`);
      setShowModal(false);
      setProjectName("");
      setProjectCode("");
      setAdminName("");
      setAdminEmail("");
    } catch (error) {
      console.error("Error al crear proyecto:", error);
      if (error.code === "auth/email-already-in-use") {
        notify("Ese email ya está registrado en Firebase Auth", true);
      } else if (error.code === "permission-denied") {
        notify("No tienes permisos para escribir en Firestore.", true);
      } else {
        notify("Error al crear el proyecto. Revisa la consola.", true);
      }
    } finally {
      setSending(false);
    }
  };

  // ===== 10. Eliminar proyecto =====
  const handleDeleteProject = async (id) => {
    const projectToDelete = projects.find((p) => p.id === id);
    if (!projectToDelete) return;
    if (
      !window.confirm(
        `¿Seguro que quieres eliminar el proyecto "${projectToDelete.name}" y todos sus datos asociados?`
      )
    )
      return;
    try {
      setSending(true);
      const subcollections = [
        "suppliers",
        "accounts",
        "users",
        "departamentos",
      ];
      for (const sub of subcollections) {
        const subRef = collection(db, "projects", projectToDelete.code, sub);
        const snapshot = await getDocs(subRef);
        await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
      }
      await deleteDoc(doc(db, "projects", projectToDelete.code));
      notify(`Proyecto "${projectToDelete.name}" eliminado correctamente`);
    } catch (error) {
      console.error("Error al eliminar proyecto:", error);
      notify("Error al eliminar el proyecto. Revisa la consola.", true);
    } finally {
      setSending(false);
    }
  };

  // ===== 11. Entrar al proyecto =====
  const handleEnterProject = (project, role) => {
    const projectData = { ...project };
    if (["HOD", "Coordinator", "Crew"].includes(role)) {
      setSelectedProject(projectData);
      setSelectedRole(role);
      setShowDeptModal(true);
    } else {
      onEnterProject(projectData, role);
    }
  };

  const confirmEnterWithDept = () => {
    if (!selectedDept) {
      notify("Selecciona un departamento", true);
      return;
    }
    onEnterProject(selectedProject, selectedRole, selectedDept);
    setShowDeptModal(false);
    setSelectedDept("");
  };

  // ===== 12. Render =====
  return (
    <section
      className="container"
      style={{
        padding: "40px 0",
        maxWidth: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 800,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h2>Proyectos</h2>
        <button
          className="button button--primary"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          onClick={() => setShowModal(true)}
        >
          <PlusCircle size={16} /> Crear proyecto
        </button>
      </div>

      {/* === LISTA DE PROYECTOS === */}
      <div className="stack" style={{ gap: 16, width: "100%", maxWidth: 800 }}>
        {projects.map((p) => (
          <div key={p.id} className="card" style={{ padding: 20 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 16,
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <strong style={{ fontSize: 16 }}>{p.name}</strong>
                  <span
                    style={{
                      fontSize: 12,
                      padding: "2px 8px",
                      border: "1px solid var(--ring)",
                      borderRadius: 999,
                      color: "var(--muted)",
                    }}
                  >
                    {p.code}
                  </span>
                </div>
                <div
                  style={{ marginTop: 6, fontSize: 13, color: "var(--muted)" }}
                >
                  Admin inicial: {p.adminName} ({p.admin}) [{p.role}]
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  className="button button--ghost"
                  onClick={() => handleDeleteProject(p.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 14,
                flexWrap: "wrap",
              }}
            >
              {["PM", "Controller", "HOD", "Coordinator", "Crew"].map((r) => (
                <button
                  key={r}
                  className="button"
                  onClick={() => handleEnterProject(p, r)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <LogIn size={14} /> {r}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* === MODAL DEPARTAMENTO === */}
      {showDeptModal && (
        <Modal
          title={`Entrar como ${selectedRole} en ${selectedProject?.name}`}
          onClose={() => setShowDeptModal(false)}
        >
          <div className="stack">
            <div className="field">
              <label>Selecciona el departamento</label>
              <select
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
              >
                <option value="">Seleccionar...</option>
                {departamentos.map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button
                className="button"
                onClick={() => setShowDeptModal(false)}
              >
                Cancelar
              </button>
              <button
                className="button button--primary"
                onClick={confirmEnterWithDept}
              >
                Entrar al proyecto
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* === MODAL CREAR PROYECTO === */}
      {showModal && (
        <Modal title="Crear nuevo proyecto" onClose={() => setShowModal(false)}>
          <form className="stack" onSubmit={handleCreateProject}>
            <div className="field">
              <label>Nombre del proyecto</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Código del proyecto</label>
              <input
                type="text"
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Nombre del administrador</label>
              <input
                type="text"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Email del administrador</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Rol del administrador</label>
              <select
                value={adminRole}
                onChange={(e) => setAdminRole(e.target.value)}
              >
                <option value="PM">PM</option>
                <option value="Controller">Controller</option>
              </select>
            </div>
            <button
              type="submit"
              className="button button--primary"
              disabled={sending}
            >
              {sending ? "Creando..." : "Crear proyecto"}
            </button>
          </form>
        </Modal>
      )}

      {/* === TOAST === */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            background: toast.error ? "#dc2626" : "var(--accent)",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {toast.msg}
        </div>
      )}
    </section>
  );
}
